import { getCSS, remCSS } from '@gimlite/common';
import classNames from 'classnames';
import { memo, useEffect } from 'react';
import { ColorType } from '../../types/component.type';
import { KeyboardShortcutEnum } from '../../types/keyboard.type';
import { Icon, IconType } from '../icon/icon.component';
import './buttonRounded.component.scss';

const sizeMapper = {
  small: {
    size: remCSS(48),
  },
  medium: {
    size: remCSS(56),
  },
} as const satisfies Record<string, { size: string }>;

const variantColorMapper = {
  tertiary: {
    iconColor: 'white_100',
    bgColor: 'white_30',
    borderColor: null,
  },
  secondary: {
    iconColor: 'white_100',
    bgColor: 'black_70',
    borderColor: 'black_70',
  },
  primary: {
    iconColor: 'white_100',
    bgColor: 'soft_teal',
    borderColor: 'soft_teal',
  },
  outline: {
    iconColor: 'black_50',
    bgColor: 'black_4',
    borderColor: 'black_15',
  },
  quaternary: {
    iconColor: 'black_50',
    bgColor: 'black_2',
    borderColor: 'black_15',
  },

} as const satisfies Record<
  string,
  {
    iconColor: ColorType;
    bgColor: ColorType | null;
    borderColor: ColorType | null;
  }
>;

export declare namespace ButtonRoundedType {
  type Props = {
    className?: string | null;
    icon: IconType.Props['variant'];
    variant?: keyof typeof variantColorMapper;
    onClick?: () => void;
    keyboardShortcut?: KeyboardShortcutEnum | string | null;
    stopPropagation?: boolean;
    size?: keyof typeof sizeMapper;
  };
}

export const ButtonRounded = memo(
  ({
    className,
    icon,
    variant = 'secondary',
    stopPropagation = true,
    onClick,
    size = 'medium',
    keyboardShortcut = null,
  }: ButtonRoundedType.Props) => {
    useEffect(() => {
      if (!keyboardShortcut || !onClick) {
        return;
      }

      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === keyboardShortcut) {
          event.preventDefault();
          onClick();
        }
      };

      window.addEventListener('keydown', handleKeyDown);

      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }, [keyboardShortcut, onClick]);

    return (
      <div
        onClick={(e) => {
          if (stopPropagation) {
            e.stopPropagation();
          }
          onClick?.();
        }}
        style={{
          borderWidth: variantColorMapper[variant].borderColor ? '1px' : '0px',
          backgroundColor: getCSS(
            `--color-${variantColorMapper[variant].bgColor}`,
          ),
          borderColor: getCSS(
            `--color-${variantColorMapper[variant].borderColor}`,
          ),
          width: `${sizeMapper[size].size}`,
          height: `${sizeMapper[size].size}`,
        }}
        className={classNames('buttonRounded', className)}
      >
        <Icon variant={icon} color={variantColorMapper[variant].iconColor} />
      </div>
    );
  },
);
